import * as React from 'react'
import ReactSelect from 'react-select'
import styled, { AnyStyledComponent } from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, utilService } from '../../../../core/services'
import { BREAKPOINT_TABLET_SMALL } from '../../../../static/constants'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import { formatDatetimeJST } from '../../../../utils/dateFormat'
import { Button, InputText, InputTextArea, Panel, Toggle } from '../../../atoms'
import { Form } from '../../../molecules'
import SidebarLayout from '../../_layouts/sidebarLayout'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

const FIELDS = {
  title: 'title',
  body: 'body',
  published_at: 'published_at',
}

const title = I18n.t('admin.edit', {
  model: I18n.t('guide_announcement', { scope: 'activerecord.models' }),
})

interface IProps {
  guide_announcement: IJsonResponse
  categories: IJsonResponse
  user: IJsonResponse
}

const selectCustomStyles = {
  control: (provided, state) => ({
    ...provided,
    border: `1px solid ${constants.COLORS.divider}`,
    borderRadius: 4,
    '&:hover': { border: `1px solid ${constants.COLORS.primary}` },
  }),
}

const AgentGuideAnnouncementEdit: React.FC<IProps> = props => {
  const { data: userData } = utilService.getDataFromJson(props.user)
  const { data: announcement } = agentService.getDataFromJson(props.guide_announcement)
  const { data: categories } = utilService.getDataFromJson(props.categories)

  const categoryOptions = categories.map(category => ({
    value: category.id,
    label: category.display_name,
  }))

  const defaultCategory = announcement.category
    ? categoryOptions.find(opt => opt.value === announcement.category.id) || null
    : null

  const [selectedCategory, setSelectedCategory] = React.useState(defaultCategory)
  const [categoryError, setCategoryError] = React.useState(false)
  const [isActive, setIsActive] = React.useState(announcement.is_active)
  const [isAnnouncement, setIsAnnouncement] = React.useState(announcement.is_announcement)
  const [isEmail, setIsEmail] = React.useState(announcement.is_email)
  const [isMobilePush, setIsMobilePush] = React.useState(announcement.is_mobile_push)

  const handleSubmit = React.useCallback(
    async (initialValues, values) => {
      if (!selectedCategory) {
        setCategoryError(true)
        return
      }
      setCategoryError(false)

      const result = { ...values }
      result.is_active = isActive
      result.is_announcement = isAnnouncement
      result.is_email = isEmail
      result.is_mobile_push = isMobilePush
      result.category_id = selectedCategory ? Number(selectedCategory.value) : null
      const { flush } = await agentService.updateGuideAnnouncement(announcement.id, result)
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      location.href = '/agent/guide_announcements'
    },
    [isActive, isAnnouncement, isEmail, isMobilePush, selectedCategory]
  )

  return (
    <SidebarLayout user={userData}>
      <Wrapper>
        <Panel title={title}>
          <Form fields={FIELDS} handleSubmit={handleSubmit}>
            <InputText
              required={true}
              name="title"
              defaultValue={announcement.title || ''}
              label={I18n.t('generic.title')}
            />
            <S.FormItemWrapper>
              <InputTextArea
                required={true}
                name="body"
                defaultValue={announcement.body || ''}
                label="本文"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <S.ReactSelectWrapper>
                <p className="ReactSelect_Title">
                  カテゴリ<S.RequiredMark>*</S.RequiredMark>
                </p>
                <div className="ReactSelect_Main">
                  <ReactSelect
                    placeholder="カテゴリを選択"
                    options={categoryOptions}
                    isMulti={false}
                    value={selectedCategory}
                    onChange={value => {
                      setSelectedCategory(value)
                      setCategoryError(false)
                    }}
                    styles={{
                      ...selectCustomStyles,
                      control: (provided, state) => ({
                        ...selectCustomStyles.control(provided, state),
                        ...(categoryError ? { borderColor: '#e74c3c' } : {}),
                      }),
                    }}
                    menuPortalTarget={document.querySelector('body')}
                  />
                  {categoryError && <S.ErrorMessage>カテゴリを選択してください</S.ErrorMessage>}
                </div>
              </S.ReactSelectWrapper>
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_active"
                checked={isActive}
                onChangeHandler={() => setIsActive(!isActive)}
                label="Active"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_announcement"
                checked={isAnnouncement}
                onChangeHandler={() => setIsAnnouncement(!isAnnouncement)}
                label="アプリお知らせ"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_email"
                checked={isEmail}
                onChangeHandler={() => setIsEmail(!isEmail)}
                label="メール通知"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <Toggle
                name="is_mobile_push"
                checked={isMobilePush}
                onChangeHandler={() => setIsMobilePush(!isMobilePush)}
                label="プッシュ通知"
              />
            </S.FormItemWrapper>
            <S.FormItemWrapper>
              <InputText
                required={false}
                name="published_at"
                defaultValue={
                  announcement.published_at
                    ? formatDatetimeJST(announcement.published_at).replace(/\s*\(.\)\s*/, ' ')
                    : ''
                }
                label="公開日時"
                placeholder="2025-01-01 00:00"
              />
            </S.FormItemWrapper>
            <div className="Buttons">
              <Button>
                <a href="/agent/guide_announcements">
                  <SvgIcon
                    name="keyboard_arrow_left"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  {I18n.t('generic.back_to_index')}
                </a>
              </Button>
              <Button primary={true}>{I18n.t('generic.update')}</Button>
            </div>
          </Form>
        </Panel>
      </Wrapper>
    </SidebarLayout>
  )
}

const S: { [key: string]: AnyStyledComponent } = {}

S.FormItemWrapper = styled.div`
  margin-top: 24px;
`

S.RequiredMark = styled.span`
  color: #e74c3c;
  margin-left: 4px;
`

S.ErrorMessage = styled.p`
  color: #e74c3c;
  font-size: 13px;
  margin-top: 4px;
`

S.ReactSelectWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
    flex-direction: column;
  }
  .ReactSelect_Title {
    min-width: 240px;
    font-size: 15px;
    font-weight: bold;
    line-height: 1;
    margin-top: 8px;

    @media (max-width: ${BREAKPOINT_TABLET_SMALL}px) {
      width: 100%;
      margin-bottom: 12px;
    }
  }
  .ReactSelect_Main {
    flex: 1;
    font-size: 15px;
  }
`

const Wrapper = styled.div`
  width: 100%;
  padding: 22px 40px 80px 46px;

  .Panel {
    max-width: none;
  }

  .Buttons {
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .Button[type='submit'] {
      width: 160px;
    }

    .Button[type='button'] {
      position: absolute;
      left: 0;
      top: 0;
      display: flex;
      align-items: center;

      .icon {
        margin-left: -10px;
      }
    }
  }

  .InputText + .InputText,
  .InputTextArea + .InputTextArea,
  .InputText + .InputTextArea,
  .InputTextArea + .InputText,
  .InputText + .CheckBox,
  .CheckBox + .CheckBox {
    margin-top: 24px;
  }

  @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
    padding: 16px 12px;
  }
`

export default AgentGuideAnnouncementEdit
